import type { Site } from "src/types/sites";
import { AppError } from "src/errors";

enum EnabledEnvironments {
  DEV = 'development',
  STAGING = 'staging',
  PRODUCTION = 'production',
}

enum EnabledSites {
  // VV = 'donate.veritysvillage.com',
  VV = 'donate.mamabearcare.org',
  STS = 'donate.savethestorks.org',
  STS_OLD = 'donate.savethestorks.com',
  FEW = 'donate.foreverywoman.org',
  IHH = 'donate.ihhministry.org',
  CF = 'donate.carefamilies.org'
}

const useLocal = import.meta.env.DEV || location.host.includes('localhost')

/**
 * Redirect map for domain migrations
 * Old domain -> New domain (will force redirect)
 */
const domainRedirects: Record<string, string> = {
  'donate.savethestorks.com': 'donate.savethestorks.org',
}

let siteDomains = [
  /** 
   * Production Domains
   */
  {
    // domain: 'donate.veritysvillage.com',
    domain: 'donate.mamabearcare.org',
    site: EnabledSites.VV,
    environment: EnabledEnvironments.PRODUCTION
  },
  {
    domain: 'donate.savethestorks.org',
    site: EnabledSites.STS,
    environment: EnabledEnvironments.PRODUCTION
  },
  {
    domain: 'donate.savethestorks.com',
    site: EnabledSites.STS,
    environment: EnabledEnvironments.PRODUCTION,
    redirect: true, // Will redirect to .org
  },
  {
    domain: 'donate.foreverywoman.org',
    site: EnabledSites.FEW,
    environment: EnabledEnvironments.PRODUCTION
  },
  {
    domain: 'donate.ihhministry.org',
    site: EnabledSites.IHH,
    environment: EnabledEnvironments.PRODUCTION
  },
  {
    domain: 'donate.carefamilies.org',
    site: EnabledSites.CF,
    environment: EnabledEnvironments.PRODUCTION
  },

  /** 
   * Staging Domains
   */
  {
    // domain: 'verity.staging.sukow.org',
    domain: 'staging.mamabearcare.sukow.org',
    site: EnabledSites.VV,
    environment: EnabledEnvironments.STAGING
  },
  {
    domain: 'storks.staging.sukow.org',
    site: EnabledSites.STS,
    environment: EnabledEnvironments.STAGING
  },
  {
    domain: 'few.donate.staging.sukow.org',
    site: EnabledSites.FEW,
    environment: EnabledEnvironments.STAGING
  },
  {
    domain: 'inhishands.staging.sukow.org',
    site: EnabledSites.IHH,
    environment: EnabledEnvironments.STAGING
  },
  {
    domain: 'staging.carefamilies.sukow.org',
    site: EnabledSites.CF,
    environment: EnabledEnvironments.STAGING
  },

  /** 
   * TODO: Dev Domains configuration
   */
  // {
  //   domain: `verity.localhost.sukow.org:${import.meta.env.VITE_PORT}`,
  //   site: EnabledSites.VV,
  //   environment: EnabledEnvironments.DEV
  // },
  // {
  //   domain: `storks.localhost.sukow.org:${import.meta.env.VITE_PORT}`,
  //   site: EnabledSites.STS,
  //   environment: EnabledEnvironments.DEV
  // },
  // {
  //   domain: `few.donate.localhost.sukow.org:${import.meta.env.VITE_PORT}`,
  //   site: EnabledSites.FEW,
  //   environment: EnabledEnvironments.DEV
  // },
]

/** 
 * Local Domain or Vite Preview
 */
if (useLocal) {
  console.log(import.meta.env)
  const viteHost = import.meta.env.VITE_HOST
  siteDomains.push({
    domain: viteHost,
    site: viteHost as EnabledSites,
    environment: EnabledEnvironments.DEV
  })
}

/**
 * Shared Config for Services on Site
 */
const shared = {
  localStorageSuffix: '_state_key',
  services: {
    anet: {
      loginId: '42fXyc5tRxJ',
      clientKey: '9z2evfw74J9vkYdpB2Cv4vLq9Wv2dNjr9m6ehjJB6ZARpyzdWEjvq7EAhKWXSL8S',
    },
    googleRecaptcha: '6LcOCckaAAAAAH9u4DWdZ8Wdxz64zy_3oTDpysnV',
    googleRecaptcha_v2: '6LeG9HMcAAAAAHGUlZ4t60TIgyk3FuDe3p1MsgLc', // sk 6LeG9HMcAAAAABeLcUpdAOcNhsoZtxoNrWbueL-a
  },
  sandbox: {
    anet: {
      loginId: '4Q9p4tzQ568',
      clientKey: '9c8NFEkuvgj3yw2eS3WYseWu895P6NRrEfGcnCTdxDb7g3AL3L562VRHusmcLamZ',
    },
    googleRecaptcha: '6LcOCckaAAAAAH9u4DWdZ8Wdxz64zy_3oTDpysnV',
    googleRecaptcha_v2: '6LeG9HMcAAAAAHGUlZ4t60TIgyk3FuDe3p1MsgLc',
                  // '6LeG9HMcAAAAAHGUlZ4t60TIgyk3FuDe3p1MsgLc' // site key v2
                  // '6LeG9HMcAAAAABeLcUpdAOcNhsoZtxoNrWbueL' // secret v2
  }
}

/**
 * Full Config for Services and Host for Site
 */
export const config : Record<string, Site> = {
  /** 
   * For Every Women Site Config
   */
  [EnabledSites.FEW]: {
    title: 'For Every Women - Donation & Payment',
    gtmId: '',
    ...shared,
    site: 'FEW',
    prefix: 'few',
    host: {
      server: 'https://donate.foreverywoman.org',
      home: 'https://donate.foreverywoman.org',
      cookie: '.foreverywoman.org',
      whitelist: 'donate.savethestorks.com,donate.mamabearcare.org,donate.veritysvillage.com,storks.staging.sukow.org,staging.mamabearcare.sukow.org,verity.staging.sukow.org'.split(','),
      receipts: 'https://donate.foreverywoman.org',
    },
    services: {
      auth0: {
        client: import.meta.env.MODE === 'production' ? 'IM1y03AIoDzn8MoXO5oQOpQ80JuRe6aQ' : 'khqvj80YFZp2xM7yBJf6DmsuCvanA6P6',
        domain: 'few.us.auth0.com',
        cookieDomain: import.meta.env.MODE === 'production' ? '.foreverywoman.org' : '.staging.sukow.org',
      },
      stripe: {
        pk: 'pk_live_51LFT0RGagZwBlmlKCoFYUeZxCLKLxCCYY1oErBsFVVRBXVYNQ7hDDSQz5FVl5UMdRJPYxTtTIR1Wy8ww9YDHbrij00i4GtPxhz',
      },
      ...shared.services
    },
    sandbox: {
      auth0: {
        client: 'piQb0qaL4qhYkpRynKzblF58lXgiJFmn',
        domain: 'few.us.auth0.com',
      },
      stripe: {
        pk: 'pk_test_51LFT0RGagZwBlmlKnOD6bbXdn1S1hxIKPLzoJQlQtcdGhyZ1ynh9rHfhkRBAWiVldeKq9ZiuZYiF6aLVGS4YJDqX00jTtpLYQ0',
      },
      ...shared.sandbox
    }
  },

  /** 
   * Verity's Village Site Config
   */
  [EnabledSites.VV]: {
    title: 'Mama Bear Care - Donation & Payment',
    gtmId: 'GTM-NRVNGVR',
    ...shared,
    site: 'VV',
    prefix: 'vv',
    host: {
      // server: 'https://donate.veritysvillage.com',
      // home: 'https://donate.veritysvillage.com',
      // cookie: '.veritysvillage.com',
      server: 'https://donate.mamabearcare.org',
      home: 'https://donate.mamabearcare.org',
      cookie: '.mamabearcare.org',
      whitelist: 'donate.savethestorks.com,donate.mamabearcare.org,donate.veritysvillage.com,storks.staging.sukow.org,staging.mamabearcare.sukow.org,verity.staging.sukow.org'.split(','),
      // receipts: 'https://donate.veritysvillage.com',
      receipts: 'https://donate.mamabearcare.org',
    },
    services: {
      auth0: {
        client: import.meta.env.MODE === 'production' ? 'n3TwPqciErgVURX4hfw6WfMSS5XT4Rx0' : 'sfM4T7CHxTztnsdKri5lwhSkI68vwZPJ',
        domain: 'savethestorks.us.auth0.com',
        organization: 'org_K7hJLrTiqSDHGRrA',
        poweredSite: true,
        cookieDomain: import.meta.env.MODE === 'production' ? '.mamabearcare.org' : '.staging.sukow.org',
      },
      stripe: {
        pk: 'pk_live_51J35QVDCYyqzHmllj23UELMwJsmihplmb6jOAsuTmEk8Aeu2k1l4O4tKRr2WwvvHiJ1WMltEB2rnXYxJIT0OhCXz00DbtMllrH',
      },
      ...shared.services
    },
    sandbox: {
      auth0: {
        client: 'Q6UeilX9FEEJLFETxH1hE0iL9vGp6Wwg',
        domain: 'savethestorks.us.auth0.com',
        organization: 'org_K7hJLrTiqSDHGRrA',
        poweredSite: true,
      },
      stripe: {
        pk: 'pk_test_51J35QVDCYyqzHmllwc5rIm80c5p4ZBYfQheIfXEksmUcMuZHvCiMG5ijT5HOohBitM4HcVl9CMiCuKZbZwFShzGV00pRUsgN7e',
      },
      ...shared.sandbox
    }
  },

  /** 
   * Save The Storks Site Config
   */
  [EnabledSites.STS]: {
    title: 'Save the Storks - Donation & Payment',
    gtmId: 'GTM-W4HFDGS',
    ...shared,
    site: 'STS',
    prefix: 'sts',
    host: {
      server: 'https://donate.savethestorks.org',
      home: 'https://donate.savethestorks.org',
      cookie: '.savethestorks.org',
      whitelist: 'donate.savethestorks.org,donate.savethestorks.com,donate.mamabearcare.org,donate.veritysvillage.com,storks.staging.sukow.org,staging.mamabearcare.sukow.org,verity.staging.sukow.org'.split(','),
      receipts: 'https://donate.savethestorks.org',
    },
    services: {
      auth0: {
        client: import.meta.env.MODE === 'production' ? 'A2JhBbySEEpvOBR8j2v8VA23wwCW1b1Z' : 'ovzzB0svO09wYAO70V8JpdEY7kPWyb2O',
        domain: 'savethestorks.us.auth0.com',
        cookieDomain: import.meta.env.MODE === 'production' ? '.savethestorks.org' : '.staging.sukow.org',
      },
      stripe: {
        pk: 'pk_live_KJeQy2Rovzk4kdI6PGkJ8B0e',
      },
      ...shared.services
    },
    sandbox: {
      auth0: {
        client: 'p1rVqu67drxHWsf6cj2s5oPCe03Rm26r',
        domain: 'savethestorks.us.auth0.com',
      },
      stripe: {
        pk: 'pk_test_BK12wxfhXsjKScJjRqp4nTXH',
      },
      ...shared.sandbox
    }
  },

  /** 
   * In His Hands Site Config
   */
  [EnabledSites.IHH]: {
    title: 'IHH Ministry - Donation & Payment',
    gtmId: 'GTM-W4HFDGS',
    ...shared,
    site: 'IHH',
    prefix: 'ihh',
    host: {
      server: 'https://donate.ihhministry.org',
      home: 'https://donate.ihhministry.org',
      cookie: '.ihhministry.org',
      whitelist: 'donate.ihhministry.org,donate.mamabearcare.org,donate.veritysvillage.com,inhishands.staging.sukow.org,staging.mamabearcare.sukow.org,verity.staging.sukow.org'.split(','),
      receipts: 'https://donate.ihhministry.org',
    },
    services: {
      auth0: {
        client: import.meta.env.MODE === 'production' ? 'YslQXntyRhMwuX4DMJulmkx8KY8rTYcE' : 'aH9M9uN8cKVHeN4ClygF52yd4KaOb02Z',
        organization: 'org_V7oKhiOI2xRlck5a',
        domain: 'savethestorks.us.auth0.com',
        poweredSite: true,
        cookieDomain: import.meta.env.MODE === 'production' ? '.ihhministry.org' : '.staging.sukow.org',
      },
      stripe: {
        pk: 'pk_live_51PO3skB5EKOixtBWX4OBdMHwBz3uxkXAbZB2iLZema2dqwssNW3nKVxNVEQVtQlbCNrmD3U0HNaQDHoLF9Sv2byB00KPD3t1LM',
      },
      ...shared.services
    },
    sandbox: {
      auth0: {
        client: 'sgmbKG7wmGT6oxT04kwptFKppr99Xg4v',
        domain: 'savethestorks.us.auth0.com',
        poweredSite: true,
        organization: 'org_V7oKhiOI2xRlck5a',
      },
      stripe: {
        pk: 'pk_test_51PO3skB5EKOixtBWad9gfkS7QNr05eM4FxmuITk1fSQwYcIOF2KDhunrhOAiE4DBH1iNBIL97kdZP8uFvn2H5mye00nUoHOZ7s',
      },
      ...shared.sandbox
    }
  },

  /** 
   * Caring Families Site Config
   */
  [EnabledSites.CF]: {
    title: 'Caring Families - Donation & Payment',
    gtmId: 'GTM-PGSBT5DL',
    ...shared,
    site: 'CF',
    prefix: 'cf',
    host: {
      server: 'https://donate.carefamilies.org',
      home: 'https://donate.carefamilies.org',
      cookie: '.carefamilies.org',
      whitelist: 'donate.carefamilies.org,donate.mamabearcare.org,donate.veritysvillage.com,inhishands.staging.sukow.org,staging.mamabearcare.sukow.org,verity.staging.sukow.org,staging.carefamilies.sukow.org'.split(','),
      receipts: 'https://donate.carefamilies.org',
    },
    services: {
      auth0: {
        client: import.meta.env.MODE === 'production' ? 'ECQVKGlsRerNZx6P206SdQPDBrLJEXA3' : 'W45m2Cj5deZ2Z9vCO2sXbutKEoLC8OCI',
        organization: 'org_E774OethwQcr6ChM',
        domain: 'savethestorks.us.auth0.com',
        poweredSite: true,
        cookieDomain: import.meta.env.MODE === 'production' ? '.carefamilies.org' : '.staging.sukow.org',
      },
      stripe: {
        pk: 'pk_live_51IP77QHDggrz3sC6U07DnUuct6gxn4p9EvXll5tnXT6BjYXxMZvYBXi8Sa37Ov6v7C7zIy8ilhVeOpn0WanS01x200oQBR6Z5T',
      },
      ...shared.services
    },
    sandbox: {
      auth0: {
        client: 'vJ5W69hFkL1am8dtnA53wfnGFVwPQIjC',
        domain: 'savethestorks.us.auth0.com',
        poweredSite: true,
        organization: 'org_E774OethwQcr6ChM',
      },
      stripe: {
        pk: 'pk_test_51IP77QHDggrz3sC6MRC5t1JbDABPc5zJYFzXQU2VaiK2zQOoKEYCQYxj04rI0hQQOhyd62J98yeksIbQ77N1ygdQ00pI0znruD',
      },
      ...shared.sandbox
    }
  },
}

/**
 * Web Worker instance using ComLink for RPCs
 */
// export const workerInstance = new ComlinkWorker<typeof import("./worker")>(
//   new URL("./worker", import.meta.url)
// );

/** 
 * Use Config [default export]
 * 
 * @returns Site configuration
 */
export const useConfig = () : Site => {
  let site: EnabledSites

  // Check for domain redirect (e.g., .com -> .org migration)
  const redirectTo = domainRedirects[location.host]
  if (redirectTo && !useLocal) {
    const newUrl = `${location.protocol}//${redirectTo}${location.pathname}${location.search}${location.hash}`
    window.location.replace(newUrl)
    // Return the STS config as a fallback while redirecting to prevent errors
    // The page will redirect before this config is actually used
    return config[EnabledSites.STS]
  }

  const sites = siteDomains.filter(site => site.environment == import.meta.env.MODE)
  if (sites.length < 1) {
    throw new Error(AppError.UNABLE_TO_RESOLVE_SITE)
  }

  console.log(sites, import.meta.env.MODE, location.host)
  if (useLocal && sites.length > 0) {
    // site = sites[0].site
    site = EnabledSites.STS
  } else {
    const resolvedSite = sites.find(site => site.domain === location.host)
    console.log(sites)
    console.log(resolvedSite)
    if (!resolvedSite) {
      throw new Error(AppError.UNABLE_TO_RESOLVE_SITE)
    }

    site = resolvedSite.site
  }

  console.log(config[site])
  return config[site]
}
export default useConfig